import React, { useCallback, useState } from "react";
import styled from "styled-components";
import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import COUNTRIES, { getCountryByCode } from "../../helpers/countries";
import { ROUTES } from "../../helpers/routes";
import { rem, toREM, vwDesktop, vwMobile } from "../../helpers/styles";
import { MEDIA_DESKTOP } from "../../helpers/theme";
import { KlaviyoService } from "../../services/klaviyo";
import { FormstackService } from "../../services/formstack";
import BackButton from "../shared/BackButton";
import Button, { ButtonType } from "../shared/Button";
import Checkbox from "../shared/Checkbox/Checkbox";
import ErrorMessage from "../shared/ErrorMessage/ErrorMessage";
import Input from "../shared/Input/Input";
import TextArea from "../shared/TextArea/TextArea";
import Select from "../StartSelling/Select";
import ReCAPTCHA from "react-google-recaptcha";
import { useMessages } from "../../hooks/useMessages";
import { pushGtmDataLayer } from "../../helpers/analytics";

export type InternationalSalesFormProps = {
  backLabel?: string;
  internationalInquiry?: {
    successMessage: string;
    disclaimer: string;
    button: string;
    distributorsPlaceholder: string;
    productsPlaceholder: string;
    countryPlaceholder: string;
    businessNamePlaceholder: string;
    phonePlaceholder: string;
    emailPlaceholder: string;
    firstNamePlaceholder: string;
    lastNamePlaceholder: string;
    klaviyoId: string;
    title: string;
  };
};

const FORM_INITIAL_STATE = {
  internationalInquiry: {
    firstName: "",
    lastName: "",
    businessName: "",
    country: "",
    phoneNumber: "",
    email: "",
    products: "",
    distributors: "",
    agreement: true,
  },
};

const InternationalSalesForm = ({
  backLabel,
  internationalInquiry,
}: InternationalSalesFormProps): React.ReactElement => {
  const recaptchaRef = React.useRef();
  const [form, setForm] = useState(FORM_INITIAL_STATE.internationalInquiry);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>();
  const messages = useMessages();

  const handleChange = (event: React.FormEvent) => {
    const { name, value, checked, type } = event.target as HTMLInputElement;
    const newForm = { ...form };
    newForm[name] = type === "checkbox" ? checked : value;
    setForm(newForm);
  };

  const isFormValid = () => {
    setErrorMessages({
      firstName: !form.firstName ? messages.fieldRequired : "",
      lastName: !form.lastName ? messages.fieldRequired : "",
      businessName: !form.businessName ? messages.fieldRequired : "",
      country: !form.country ? messages.fieldRequired : "",
      email: !form.email
        ? messages.fieldRequired
        : !isEmail(form?.email)
        ? messages.validEmail
        : "",
      phoneNumber: !form.phoneNumber
        ? messages.fieldRequired
        : !isMobilePhone(form?.phoneNumber)
        ? messages.validPhoneNumber
        : "",
      products: !form.products ? messages.fieldRequired : "",
      distributors: !form.distributors ? messages.fieldRequired : "",
      agreement: !form.agreement ? messages.fieldRequired : "",
    });

    return (
      form?.firstName &&
      form?.lastName &&
      form?.businessName &&
      form?.country &&
      isEmail(form?.email) &&
      isMobilePhone(form?.phoneNumber) &&
      form?.products &&
      form?.distributors
    );
  };

  const handleSubmit = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();

      if (!isFormValid()) return;

      try {
        await recaptchaRef.current?.executeAsync();
        if (internationalInquiry?.klaviyoId) {
          await KlaviyoService.contactSalesInternational(
            internationalInquiry?.klaviyoId,
            {
              ...form,
              country: {
                code: form?.country,
                name: getCountryByCode(form?.country)?.Name,
              },
            }
          );          
        }
        await FormstackService.contactSales({
          ...form,
          country: {
            code: form?.country,
            name: getCountryByCode(form?.country)?.Name,
          },
        });
        const tId = setTimeout(() => {
          setSuccess(false);
          setForm(FORM_INITIAL_STATE.internationalInquiry);
          clearTimeout(tId);
        }, 10000);
        setSuccess(true);

        pushGtmDataLayer({
          event: "submit_an_inquiry_finish",
          inquiry_type: internationalInquiry?.title,
        });
      } catch (error) {
        console.log(error);
      }
    },
    [form]
  );

  return (
    <FormContainer>
      <BackButton label={backLabel} link={ROUTES.CONTACT_US} />

      {success ? (
        <SuccessMessage>{internationalInquiry?.successMessage}</SuccessMessage>
      ) : (
        <FieldsWrapper>
          <Title>{internationalInquiry?.title}</Title>
          {internationalInquiry && (
            <Fields>
              <Input
                label={internationalInquiry?.firstNamePlaceholder}
                name="firstName"
                value={form?.firstName}
                onChange={handleChange}
                error={errorMessages?.firstName}
              />
              <Input
                label={internationalInquiry?.lastNamePlaceholder}
                name="lastName"
                value={form?.lastName}
                onChange={handleChange}
                error={errorMessages?.lastName}
              />
              <Input
                label={internationalInquiry?.businessNamePlaceholder}
                name="businessName"
                value={form?.businessName}
                onChange={handleChange}
                error={errorMessages?.businessName}
              />
              <Select
                aria-label={internationalInquiry?.countryPlaceholder}
                placeHolder={internationalInquiry?.countryPlaceholder}
                name="country"
                value={form?.country}
                onChange={handleChange}
                options={COUNTRIES}
                labelField="Name"
                valueField="Code"
                error={errorMessages?.country}
              />
              <Input
                label={internationalInquiry?.phonePlaceholder}
                name="phoneNumber"
                onChange={handleChange}
                value={form?.phoneNumber}
                error={errorMessages?.phoneNumber}
              />
              <Input
                label={internationalInquiry?.emailPlaceholder}
                name="email"
                onChange={handleChange}
                value={form?.email}
                error={errorMessages?.email}
              />
              <TextArea
                label={internationalInquiry?.productsPlaceholder}
                name="products"
                onChange={handleChange}
                value={form?.products}
                error={errorMessages?.products}
              />
              <TextArea
                label={internationalInquiry?.distributorsPlaceholder}
                name="distributors"
                onChange={handleChange}
                value={form?.distributors}
                error={errorMessages?.distributors}
              />
              <div>
                <Checkbox
                  name="agreement"
                  checked={form?.agreement ?? true}
                  onChange={handleChange}
                  label={internationalInquiry?.disclaimer}
                />
                {errorMessages?.agreement && (
                  <ErrorMessage
                    message={errorMessages.agreement}
                    bgType="dark"
                  />
                )}
              </div>
            </Fields>
          )}

          <FormAction>
            <Button
              onClick={handleSubmit}
              type={ButtonType.PRIMARY_LIGHT}
              label={internationalInquiry?.button || ""}
              submit
            />
          </FormAction>
        </FieldsWrapper>
      )}

      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.GATSBY_RECAPTCHA_KEY}
      />
    </FormContainer>
  );
};

export default InternationalSalesForm;

const FormAction = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${vwMobile(20)};
  margin-top: ${vwMobile(40)};
  ${MEDIA_DESKTOP} {
    justify-content: flex-end;
    gap: ${vwDesktop(20)};
    margin-top: ${vwDesktop(40)};
  }
  > * {
    flex: 1;
    ${MEDIA_DESKTOP} {
      flex: 0 0 auto;
      width: ${vwDesktop(180)};
    }
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(16)};
  ${MEDIA_DESKTOP} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${vwDesktop(16)};

    & > :nth-child(7),
    & > :nth-child(8) {
      grid-column: 1 / 3;
    }
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(30)};
  color: white;
  background-color: #012102;
  padding: ${vwMobile(40)} ${vwMobile(20)} ${vwMobile(36)};
  ${MEDIA_DESKTOP} {
    gap: ${vwDesktop(30)};
    width: 100%;
    padding: ${vwDesktop(40)} ${vwDesktop(100)} ${vwDesktop(80)};
  }
  h3 {
    font-size: ${rem(36)};
    font-weight: 900;
    text-transform: uppercase;
    margin-bottom: ${vwMobile(15)};
    letter-spacing: 0.03em;
    ${MEDIA_DESKTOP} {
      margin-bottom: ${vwDesktop(15)};
    }
  }
`;

const FieldsWrapper = styled.div`
  ${MEDIA_DESKTOP} {
    padding: 0 ${vwDesktop(105)};
  }
`;

const Title = styled.h1`
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${rem(44)};
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.012em;
  line-height: ${rem(53)};
  margin-bottom: ${vwMobile(40)};
  ${MEDIA_DESKTOP} {
    margin-bottom: ${vwDesktop(40)};
  }
`;

const Empty = styled.div`
  display: none;
  ${MEDIA_DESKTOP} {
    display: block;
  }
`;

const SuccessMessage = styled.p`
  width: 100%;
  text-align: center;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(18)}rem;
  font-weight: 800;
  letter-spacing: 0.08em;
`;
